/**
 * 全局音乐律动 — 按设备维度的灯光快照注册表（内存 + IndexedDB）。
 * 主应用 / 键盘子应用统一读写，用于：首次进律动页、灯光页调参、取消勾选、弹窗「是」、退出桌面端。
 */

import {
  isValidRoutineLightingSnapshotData,
  loadDeviceLightingSnapshotRecord,
  listAllDeviceLightingSnapshotIds,
  saveRoutineLightingSnapshotToIdb,
  deleteRoutineLightingSnapshotFromIdb,
  normalizeHfdEffect,
  type HfdRoutineEffectSnapshot,
  type RoutineLightingIdbRecord,
} from "./routineLightingSnapshotIdb";
import { resolveXingshanKeyboardLightingLamp } from "../main/xingshanDoubleLightingDevices";
import {
  coerceXingshanRoutineBaseToUiStorage,
  isPlaceholderRoutineLightingSnapshot,
  XINGSHAN_UI_PERCENT_SCALE_FLAG,
  normalizeHfdEffectForRestore,
  isHfdNonRestorableLightingMode,
  HFD_ROUTINE_FALLBACK_EFFECT,
} from "./routineLightingDefaults";
import { rhythmTargetIdLegacyBase } from "./rhythmTargetId";
import {
  equipmentFromBeiyingRoutineSnapshotMeta,
  isBeiyingWebHidCrossInterfaceRhythmFamily,
  isBeiyingWebHidCrossInterfaceSamePhysicalKeyboard,
} from "./beiyingRhythmConnection";
import {
  isHuafendaCrossInterfaceSamePhysicalKeyboard,
  isHuafendaWiredRhythmQuitFullReconnectEquipment,
} from "./hfdWirelessMusicRhythm";
import { normalizeBeiyingRoutineBaseForSnapshot } from "./routineLightingDefaults";

export type DeviceLightingSnapshot = RoutineLightingIdbRecord;

const memoryByDeviceId = new Map<string, DeviceLightingSnapshot>();

export function enrichRoutineSnapshotWithDeviceMeta(
  snap: Record<string, unknown>,
  deviceId: string,
  equipment?: Record<string, unknown> | null
): Record<string, unknown> {
  const eq = equipment ?? {};
  const solution = String(eq.solutionRovider ?? "").trim();
  return {
    ...snap,
    __rhythmDeviceId: deviceId,
    __solutionRovider: solution || snap.__solutionRovider,
    __keyboardType: eq.keyboardType ?? eq.productName ?? eq.displayName,
    __vendorId: eq.vendorId,
    __productId: eq.productId,
    __identifier: eq.identifier ?? eq.uuid ?? eq.deviceId,
  };
}

export async function readDeviceLightingSnapshot(
  deviceId: string
): Promise<DeviceLightingSnapshot | null> {
  if (!deviceId) return null;
  const cached = memoryByDeviceId.get(deviceId);
  if (
    cached?.routineBase &&
    isValidRoutineLightingSnapshotData(cached.routineBase)
  ) {
    return cached;
  }
  const fromIdb = await loadDeviceLightingSnapshotRecord(deviceId);
  if (fromIdb) {
    memoryByDeviceId.set(deviceId, fromIdb);
    return fromIdb;
  }
  return null;
}

export async function hasDeviceLightingSnapshot(
  deviceId: string
): Promise<boolean> {
  const hfd = await readDeviceHfdRoutineEffectSnapshot(deviceId);
  if (hfd) return true;
  const snap = await readDeviceLightingSnapshot(deviceId);
  return Boolean(
    snap?.routineBase && isValidRoutineLightingSnapshotData(snap.routineBase)
  );
}

export async function saveDeviceLightingSnapshot(
  record: Omit<DeviceLightingSnapshot, "updatedAt"> & { updatedAt?: number }
): Promise<void> {
  if (
    !record.deviceId ||
    !isValidRoutineLightingSnapshotData(record.routineBase)
  ) {
    return;
  }
  const equipment = record.equipment;
  const solution = String(equipment?.solutionRovider ?? "").toLowerCase();
  let routineBase = { ...record.routineBase };
  if (solution === "xingshan") {
    routineBase = coerceXingshanRoutineBaseToUiStorage(routineBase, {
      trustAsUiPercent: routineBase[XINGSHAN_UI_PERCENT_SCALE_FLAG] === true,
    });
  }
  const full: DeviceLightingSnapshot = {
    ...record,
    routineBase,
    updatedAt: record.updatedAt ?? Date.now(),
  };
  memoryByDeviceId.set(record.deviceId, full);
  await saveRoutineLightingSnapshotToIdb(record.deviceId, full.routineBase, {
    equipment: full.equipment,
    customLighting: full.customLighting,
    hfdRoutineEffect: full.hfdRoutineEffect,
  });
}

/** 灯光页调参后：只更新 routineBase（及可选 custom），保留 equipment */
export async function patchDeviceLightingRoutineBase(
  deviceId: string,
  routineBase: Record<string, unknown>,
  options?: {
    equipment?: Record<string, unknown>;
    customLighting?: unknown[] | null;
    hfdRoutineEffect?: HfdRoutineEffectSnapshot | null;
    trustAsUiPercent?: boolean;
  }
): Promise<boolean> {
  if (!deviceId) return false;
  if (!isValidRoutineLightingSnapshotData(routineBase)) {
    return Boolean(await hasDeviceLightingSnapshot(deviceId));
  }
  const prev = (await readDeviceLightingSnapshot(deviceId)) ?? {
    deviceId,
    routineBase: {},
    equipment: options?.equipment,
    updatedAt: Date.now(),
  };
  const equipment = options?.equipment ?? prev.equipment;
  const solution = String(equipment?.solutionRovider ?? "").toLowerCase();
  let normalizedBase = routineBase;
  if (solution === "xingshan") {
    normalizedBase = coerceXingshanRoutineBaseToUiStorage(routineBase, {
      trustAsUiPercent:
        routineBase[XINGSHAN_UI_PERCENT_SCALE_FLAG] === true ||
        options?.trustAsUiPercent === true,
    });
  }
  await saveDeviceLightingSnapshot({
    deviceId,
    routineBase: enrichRoutineSnapshotWithDeviceMeta(
      normalizedBase,
      deviceId,
      equipment
    ),
    equipment,
    customLighting:
      options?.customLighting !== undefined
        ? options.customLighting
        : prev.customLighting,
    hfdRoutineEffect:
      options?.hfdRoutineEffect !== undefined
        ? options.hfdRoutineEffect
        : prev.hfdRoutineEffect,
  });
  return true;
}

export type CaptureLightingSnapshotApi = {
  getLightingBase?: (p: unknown) => Promise<{ code?: number; data?: unknown }>;
  getLightingCustom?: (
    p?: unknown
  ) => Promise<{ code?: number; data?: unknown }>;
  getLEDEffect?: () => Promise<{ code?: number; data?: unknown }>;
};

/**
 * 从实体键盘 SDK 拉取当前常规灯效并写入注册表（首次进律动页 / 勾选设备时调用）。
 */
export async function captureDeviceLightingSnapshotFromSdk(options: {
  deviceId: string;
  equipment: Record<string, unknown>;
  sdkKeyboardLightingApi: CaptureLightingSnapshotApi | null | undefined;
  force?: boolean;
}): Promise<boolean> {
  const { deviceId, equipment, sdkKeyboardLightingApi, force } = options;
  if (!deviceId || !sdkKeyboardLightingApi) return false;

  if (!force && (await hasDeviceLightingSnapshot(deviceId))) {
    return true;
  }

  const solution = String(equipment.solutionRovider ?? "").toLowerCase();

  if (solution === "huafenda" && sdkKeyboardLightingApi.getLEDEffect) {
    try {
      const res = await sdkKeyboardLightingApi.getLEDEffect();
      if (res?.code === 200 && res?.data) {
        const hfd = normalizeHfdEffect(res.data);
        if (hfd) {
          if (force) {
            const existing = await readDeviceHfdRoutineEffectSnapshot(deviceId);
            const sessionExisting =
              readKeyboardSubAppHfdRoutineEffectFromSession();
            const keepExisting =
              (existing && existing.mode !== hfd.mode) ||
              (sessionExisting && sessionExisting.mode !== hfd.mode);
            if (keepExisting) {
              const preferred = sessionExisting ?? existing;
              if (preferred) {
                await saveDeviceLightingSnapshot({
                  deviceId,
                  routineBase: { mode: preferred.mode, open: "Open" },
                  equipment: { ...equipment },
                  hfdRoutineEffect: preferred,
                });
                return true;
              }
            }
          }
          await saveDeviceLightingSnapshot({
            deviceId,
            routineBase: { mode: hfd.mode, open: "Open" },
            equipment: { ...equipment },
            hfdRoutineEffect: hfd,
          });
          return true;
        }
      }
    } catch (e) {
      console.warn(
        "[LightingSnapshotRegistry] 华奋达拉取灯效失败:",
        deviceId,
        e
      );
    }
    return false;
  }

  let routineBase: Record<string, unknown> | null = null;
  let customLighting: unknown[] | null = null;

  const getLightingBase = sdkKeyboardLightingApi.getLightingBase;
  if (getLightingBase) {
    try {
      const res = await getLightingBase({
        area: "Keyboard",
        config: "Base",
        lamp: resolveXingshanKeyboardLightingLamp(equipment),
      });
      if (res?.code === 200 && res.data && typeof res.data === "object") {
        let data = res.data as Record<string, unknown>;
        if (solution === "xingshan") {
          data = coerceXingshanRoutineBaseToUiStorage(data);
        } else if (solution === "beiying") {
          data = normalizeBeiyingRoutineBaseForSnapshot(data);
        }
        if (isValidRoutineLightingSnapshotData(data)) {
          routineBase = enrichRoutineSnapshotWithDeviceMeta(
            data,
            deviceId,
            equipment
          );
        }
      }
    } catch (e) {
      console.warn(
        "[LightingSnapshotRegistry] getLightingBase 失败:",
        deviceId,
        e
      );
    }
  }

  const getLightingCustom = sdkKeyboardLightingApi.getLightingCustom;
  if (getLightingCustom) {
    try {
      const res = await getLightingCustom();
      if (res?.code === 200 && Array.isArray(res.data)) {
        customLighting = res.data;
      }
    } catch {
      // ignore
    }
  }

  if (!routineBase) {
    const existing = await readDeviceLightingSnapshot(deviceId);
    return Boolean(
      existing?.routineBase &&
      isValidRoutineLightingSnapshotData(existing.routineBase)
    );
  }

  await saveDeviceLightingSnapshot({
    deviceId,
    routineBase,
    equipment: { ...equipment },
    customLighting,
  });
  writePreRhythmRoutineSnapshotForDevice(deviceId, routineBase);
  return true;
}

export async function listDeviceLightingSnapshotIds(): Promise<string[]> {
  const fromMemory = [...memoryByDeviceId.keys()];
  const fromIdb = await listAllDeviceLightingSnapshotIds();
  return [...new Set([...fromMemory, ...fromIdb])];
}

export async function clearDeviceLightingSnapshot(
  deviceId: string
): Promise<void> {
  memoryByDeviceId.delete(deviceId);
  await deleteRoutineLightingSnapshotFromIdb(deviceId);
}

export async function readDeviceRoutineBaseSnapshot(
  deviceId: string
): Promise<Record<string, unknown> | null> {
  const rec = await readDeviceLightingSnapshot(deviceId);
  return rec?.routineBase ?? null;
}

export async function readDeviceCustomLightingSnapshot(
  deviceId: string
): Promise<unknown[] | null> {
  const rec = await readDeviceLightingSnapshot(deviceId);
  return Array.isArray(rec?.customLighting) ? rec.customLighting : null;
}

export async function readDeviceHfdRoutineEffectSnapshot(
  deviceId: string
): Promise<HfdRoutineEffectSnapshot | null> {
  const rec = await readDeviceLightingSnapshot(deviceId);
  const hfd = rec?.hfdRoutineEffect;
  if (!hfd || isHfdNonRestorableLightingMode(hfd.mode)) return null;
  return hfd;
}

/**
 * 恢复用：精确 deviceId 未命中时，扫描同型号华奋达有线/2.4G 快照（X87 V2 等）。
 */
export async function readDeviceHfdRoutineEffectSnapshotForRestore(
  deviceId: string,
  equipment?: Record<string, unknown> | null
): Promise<HfdRoutineEffectSnapshot | null> {
  const direct = await readDeviceHfdRoutineEffectSnapshot(deviceId);
  if (direct) return direct;

  const legacy = rhythmTargetIdLegacyBase(deviceId);
  if (legacy !== deviceId) {
    const fromLegacy = await readDeviceHfdRoutineEffectSnapshot(legacy);
    if (fromLegacy) return fromLegacy;
  }

  if (!equipment || String(equipment.solutionRovider ?? "") !== "huafenda") {
    return null;
  }

  const storedIds = await listAllDeviceLightingSnapshotIds();
  let best: HfdRoutineEffectSnapshot | null = null;
  let bestUpdated = 0;
  for (const sid of storedIds) {
    if (sid === deviceId || rhythmTargetIdLegacyBase(sid) === legacy) continue;
    const rec = await readDeviceLightingSnapshot(sid);
    const hfd = rec?.hfdRoutineEffect;
    if (!hfd || isHfdNonRestorableLightingMode(hfd.mode)) continue;
    const recEq = (rec.equipment ?? {}) as Record<string, unknown>;
    if (!isHuafendaCrossInterfaceSamePhysicalKeyboard(equipment, recEq)) {
      continue;
    }
    const updated = rec.updatedAt ?? 0;
    if (updated >= bestUpdated) {
      best = hfd;
      bestUpdated = updated;
    }
  }
  return best;
}

/** 子应用灯光页写入的华奋达常规灯效 session（主/子应用同源可读） */
export const KEYBOARD_SUB_APP_HFD_ROUTINE_EFFECT_SESSION_KEY =
  "keyboard-management:lighting:lastHfdRoutineEffect";

export function readKeyboardSubAppHfdRoutineEffectFromSession(): HfdRoutineEffectSnapshot | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(
      KEYBOARD_SUB_APP_HFD_ROUTINE_EFFECT_SESSION_KEY
    );
    if (!raw) return null;
    const parsed = JSON.parse(raw) as HfdRoutineEffectSnapshot;
    if (!parsed || isHfdNonRestorableLightingMode(parsed.mode)) return null;
    return parsed;
  } catch {
    return null;
  }
}

async function resolveBestExistingHfdRoutineSnapshot(
  deviceId: string,
  equipment?: Record<string, unknown> | null
): Promise<HfdRoutineEffectSnapshot | null> {
  const session = readKeyboardSubAppHfdRoutineEffectFromSession();
  if (session) return session;
  return readDeviceHfdRoutineEffectSnapshotForRestore(deviceId, equipment);
}

/** 子应用华奋达灯光页：硬件恢复完成后刷新 UI（getLEDEffect 回读） */
export const HFD_ROUTINE_LIGHTING_RESTORED_EVENT =
  "keyboard-management:hfd-routine-lighting-restored";

/**
 * 神光/律动恢复常规后：灯光页首读须忽略过期的 lastHfdUiMode=20（自定义），
 * 与 `keyboard-management` session key 对齐。
 */
export const HFD_POST_ROUTINE_RESTORE_UI_SYNC_KEY =
  "keyboard-management:lighting:hfdPostRoutineRestore";

/** 与子应用 lightingSnapshotStore.LAST_HFD_UI_LIGHTING_MODE_KEY 对齐 */
const HFD_LAST_UI_LIGHTING_MODE_KEY =
  "keyboard-management:lighting:lastHfdUiMode";

export type HfdRoutineLightingRestoredDetail = {
  mode?: number;
};

function isHfdCustomUiModeValue(mode: unknown): boolean {
  const n = Number(mode);
  return n === 20 || n === 0x80 || n === 128;
}

/** 恢复常规后同步 UI 偏好，避免灯光页仍钉在「自定义」 */
export function markHfdPostRoutineRestoreUiSync(mode?: unknown): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(HFD_POST_ROUTINE_RESTORE_UI_SYNC_KEY, "1");
    const n = Number(mode);
    if (Number.isFinite(n) && !isHfdCustomUiModeValue(n)) {
      window.sessionStorage.setItem(HFD_LAST_UI_LIGHTING_MODE_KEY, String(n));
    }
  } catch {
    // ignore
  }
}

/** 灯光页 getCurrentLightingEffect：消费一次，本轮勿用过期 preferCustom */
export function consumeHfdPostRoutineRestoreUiSync(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (
      window.sessionStorage.getItem(HFD_POST_ROUTINE_RESTORE_UI_SYNC_KEY) !==
      "1"
    ) {
      return false;
    }
    window.sessionStorage.removeItem(HFD_POST_ROUTINE_RESTORE_UI_SYNC_KEY);
    return true;
  } catch {
    return false;
  }
}

export function dispatchHfdRoutineLightingRestoredEvent(
  detail?: HfdRoutineLightingRestoredDetail
): void {
  if (typeof window === "undefined") return;
  markHfdPostRoutineRestoreUiSync(detail?.mode);
  try {
    window.dispatchEvent(
      new CustomEvent(HFD_ROUTINE_LIGHTING_RESTORED_EVENT, {
        detail: detail ?? {},
      })
    );
  } catch {
    // ignore
  }
}

const HFD_ROUTINE_RESTORE_RETRY_MS = 280;
const HFD_ROUTINE_RESTORE_READ_SETTLE_MS = 150;

export type HfdRoutineLightingSdkApi = {
  setLEDEffect?: (
    params: HfdRoutineEffectSnapshot
  ) => Promise<{ code?: number; data?: unknown } | unknown>;
  getLEDEffect?: () => Promise<{ code?: number; data?: unknown } | unknown>;
};

function normalizeHfdDeviceModeForCompare(mode: unknown): number {
  const n = Number(mode);
  if (n === 0x80 || n === 128) return 20;
  return n;
}

function isHfdRoutineRestoreWriteSuccess(
  res: { code?: number; data?: unknown } | unknown
): boolean {
  if (!res || typeof res !== "object") return false;
  const code = (res as { code?: number }).code;
  return code === 200 || code === 0;
}

/** 解析退出律动后要写回的华奋达常规灯效（session → IDB → 兜底） */
export async function resolveHfdRoutineEffectForHardwareRestore(
  deviceId: string | null | undefined,
  equipment?: Record<string, unknown> | null
): Promise<HfdRoutineEffectSnapshot> {
  const session = readKeyboardSubAppHfdRoutineEffectFromSession();
  if (session && !isHfdNonRestorableLightingMode(session.mode)) {
    return session;
  }
  if (deviceId) {
    const fromIdb = await readDeviceHfdRoutineEffectSnapshotForRestore(
      deviceId,
      equipment
    );
    if (fromIdb) return fromIdb;
  }
  return HFD_ROUTINE_FALLBACK_EFFECT;
}

/**
 * 写华奋达常规灯效并在可能时 getLEDEffect 校验（避免 SDK 返回成功但固件仍停在律动态）。
 */
export async function writeHfdRoutineEffectToHardwareWithVerify(
  api: HfdRoutineLightingSdkApi | null | undefined,
  effect: HfdRoutineEffectSnapshot,
  options?: {
    maxAttempts?: number;
    retryMs?: number;
    equipment?: Record<string, unknown> | null;
  }
): Promise<boolean> {
  if (!api?.setLEDEffect) return false;

  const payload = normalizeHfdEffectForRestore(effect);
  const expectedMode = normalizeHfdDeviceModeForCompare(payload.mode);
  const isWiredFullReconnectQuit =
    isHuafendaWiredRhythmQuitFullReconnectEquipment(options?.equipment);
  const maxAttempts = Math.max(
    1,
    options?.maxAttempts ?? (isWiredFullReconnectQuit ? 6 : 4)
  );
  const retryMs =
    options?.retryMs ??
    (isWiredFullReconnectQuit ? 320 : HFD_ROUTINE_RESTORE_RETRY_MS);
  const readSettleMs = isWiredFullReconnectQuit
    ? 220
    : HFD_ROUTINE_RESTORE_READ_SETTLE_MS;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    if (attempt > 0) {
      await new Promise((r) => setTimeout(r, retryMs * attempt));
    }
    try {
      const writeRes = await api.setLEDEffect(payload);
      if (!isHfdRoutineRestoreWriteSuccess(writeRes)) {
        continue;
      }
      if (!api.getLEDEffect) return true;

      await new Promise((r) => setTimeout(r, readSettleMs));
      const readRes = await api.getLEDEffect();
      if (!isHfdRoutineRestoreWriteSuccess(readRes)) continue;
      const data = (readRes as { data?: { mode?: unknown } }).data;
      const rawMode = Number(data?.mode);
      if (!Number.isFinite(rawMode)) return true;
      if (isHfdNonRestorableLightingMode(rawMode)) continue;
      if (normalizeHfdDeviceModeForCompare(rawMode) === expectedMode) {
        return true;
      }
    } catch (e) {
      console.warn(
        "[LightingSnapshotRegistry] 华奋达 setLEDEffect 恢复重试失败:",
        attempt + 1,
        e
      );
    }
  }
  return false;
}

/** 律动退出后：按快照写实体灯效（带校验重试） */
export async function restoreHuafendaRoutineLightingToHardwareVerified(
  api: HfdRoutineLightingSdkApi | null | undefined,
  deviceId: string | null | undefined,
  equipment?: Record<string, unknown> | null,
  options?: { maxAttempts?: number; retryMs?: number }
): Promise<boolean> {
  const effect = await resolveHfdRoutineEffectForHardwareRestore(
    deviceId,
    equipment
  );
  return writeHfdRoutineEffectToHardwareWithVerify(api, effect, {
    ...options,
    equipment,
  });
}

/** 华奋达：按 deviceId 读 IndexedDB hfdRoutineEffect 并 setLEDEffect 恢复（与星闪 per-device 快照恢复对齐） */
export async function restoreHuafendaRoutineLightingFromDeviceSnapshot(
  sdkKeyboardLightingApi: HfdRoutineLightingSdkApi | null | undefined,
  deviceId: string,
  equipment?: Record<string, unknown> | null
): Promise<boolean> {
  if (!sdkKeyboardLightingApi?.setLEDEffect || !deviceId) return false;

  const hfd = await resolveBestExistingHfdRoutineSnapshot(deviceId, equipment);
  if (!hfd) return false;

  if (isHfdNonRestorableLightingMode(hfd.mode)) return false;

  const ok = await writeHfdRoutineEffectToHardwareWithVerify(
    sdkKeyboardLightingApi,
    hfd
  );
  if (ok) {
    dispatchHfdRoutineLightingRestoredEvent({ mode: Number(hfd.mode) });
  } else {
    console.warn("[LightingSnapshotRegistry] 华奋达恢复灯效失败:", deviceId);
  }
  return ok;
}

/** 恢复前校验：快照须属于该 deviceId / 方案商 / 型号，避免星闪误用贝盈全局缓存 */
export function snapshotRoutineBaseMatchesEquipment(
  routineBase: Record<string, unknown> | null | undefined,
  equipment: Record<string, unknown> | null | undefined,
  deviceId?: string
): boolean {
  if (!routineBase || typeof routineBase !== "object") return false;

  const eq = equipment ?? {};
  const snapSolution = String(
    routineBase.__solutionRovider ?? eq.solutionRovider ?? ""
  )
    .trim()
    .toLowerCase();

  if (deviceId) {
    const snapDeviceId = String(routineBase.__rhythmDeviceId ?? "").trim();
    if (
      snapDeviceId &&
      snapDeviceId !== deviceId &&
      rhythmTargetIdLegacyBase(snapDeviceId) !==
        rhythmTargetIdLegacyBase(deviceId)
    ) {
      const snapEq = equipmentFromBeiyingRoutineSnapshotMeta(
        routineBase,
        snapSolution || "beiying"
      );
      if (
        !isBeiyingWebHidCrossInterfaceSamePhysicalKeyboard(
          equipment ?? {},
          snapEq
        )
      ) {
        return false;
      }
    }
  }

  const expectedSolution = String(eq.solutionRovider ?? "")
    .trim()
    .toLowerCase();
  if (expectedSolution && snapSolution && snapSolution !== expectedSolution) {
    return false;
  }

  const expectedVid = Number(eq.vendorId);
  const expectedPid = Number(eq.productId);
  const snapVid = Number(routineBase.__vendorId ?? routineBase.vendorId);
  const snapPid = Number(routineBase.__productId ?? routineBase.productId);
  if (
    Number.isFinite(expectedVid) &&
    Number.isFinite(snapVid) &&
    expectedVid !== snapVid
  ) {
    return false;
  }
  if (
    Number.isFinite(expectedPid) &&
    Number.isFinite(snapPid) &&
    expectedPid !== snapPid
  ) {
    const snapEq = equipmentFromBeiyingRoutineSnapshotMeta(
      routineBase,
      snapSolution || "beiying"
    );
    if (
      !isBeiyingWebHidCrossInterfaceSamePhysicalKeyboard(eq, snapEq) &&
      !isHuafendaCrossInterfaceSamePhysicalKeyboard(eq, snapEq)
    ) {
      return false;
    }
  }

  /** 贝盈设备：拒绝明显是星闪结构的快照 */
  if (expectedSolution === "beiying") {
    if (
      routineBase.selectStaticColor !== undefined &&
      routineBase.luminance !== undefined &&
      routineBase.colorMode === undefined
    ) {
      return false;
    }
  }

  return true;
}

function pickRoutineBaseForDeviceRestore(
  snap: Record<string, unknown> | null | undefined,
  deviceId: string,
  equipment?: Record<string, unknown> | null
): Record<string, unknown> | null {
  if (!snap || !isValidRoutineLightingSnapshotData(snap)) return null;
  if (Number(snap.mode) === 20) return null;
  if (snap.open === "Close") return null;
  if (isPlaceholderRoutineLightingSnapshot(snap)) return null;
  if (!snapshotRoutineBaseMatchesEquipment(snap, equipment, deviceId)) {
    return null;
  }
  return snap;
}

/**
 * 仅按 deviceId 读取常规灯效快照（IndexedDB / 内存），禁止回退全局 lastRoutineBase。
 */
export async function readPerDeviceRoutineSnapshotForRestore(
  deviceId: string,
  equipment?: Record<string, unknown> | null
): Promise<Record<string, unknown> | null> {
  if (!deviceId) return null;

  const preRhythm = readPreRhythmRoutineSnapshotForDevice(deviceId);
  const fromPreRhythm = pickRoutineBaseForDeviceRestore(
    preRhythm,
    deviceId,
    equipment
  );
  if (fromPreRhythm) return fromPreRhythm;

  const tryId = async (id: string): Promise<Record<string, unknown> | null> => {
    const rec = await readDeviceLightingSnapshot(id);
    const eq = equipment ?? rec?.equipment ?? null;
    return pickRoutineBaseForDeviceRestore(rec?.routineBase ?? null, id, eq);
  };

  let snap = await tryId(deviceId);
  if (snap) return snap;

  const legacy = rhythmTargetIdLegacyBase(deviceId);
  if (legacy !== deviceId) {
    snap = await tryId(legacy);
    if (snap) return snap;
  }

  const storedIds = await listAllDeviceLightingSnapshotIds();
  const base = rhythmTargetIdLegacyBase(deviceId);
  for (const sid of storedIds) {
    if (rhythmTargetIdLegacyBase(sid) !== base) continue;
    snap = await tryId(sid);
    if (snap) return snap;
  }

  snap = readPerDeviceRoutineFromSession(deviceId, equipment);
  if (snap) return snap;

  if (equipment && isBeiyingWebHidCrossInterfaceRhythmFamily(equipment)) {
    let bestSnap: Record<string, unknown> | null = null;
    let bestUpdated = 0;
    for (const sid of storedIds) {
      const rec = await readDeviceLightingSnapshot(sid);
      if (!rec?.routineBase) continue;
      const recEq = (rec.equipment ?? {}) as Record<string, unknown>;
      const metaEq = equipmentFromBeiyingRoutineSnapshotMeta(
        rec.routineBase as Record<string, unknown>,
        String(recEq.solutionRovider ?? "beiying")
      );
      if (
        !isBeiyingWebHidCrossInterfaceSamePhysicalKeyboard(equipment, recEq) &&
        !isBeiyingWebHidCrossInterfaceSamePhysicalKeyboard(equipment, metaEq)
      ) {
        continue;
      }
      const picked = pickRoutineBaseForDeviceRestore(
        rec.routineBase,
        deviceId,
        equipment
      );
      if (picked && rec.updatedAt >= bestUpdated) {
        bestSnap = picked;
        bestUpdated = rec.updatedAt;
      }
    }
    if (bestSnap) return bestSnap;
  }

  return null;
}

const PRE_RHYTHM_ROUTINE_SESSION_PREFIX =
  "keyboard-management:lighting:preRhythmRoutine:";

/** 进入律动前写入：退出恢复时优先于 IndexedDB 旧快照 */
export function writePreRhythmRoutineSnapshotForDevice(
  deviceId: string,
  snap: Record<string, unknown>
): void {
  if (typeof window === "undefined" || !deviceId || !snap) return;
  if (!isValidRoutineLightingSnapshotData(snap)) return;
  if (isPlaceholderRoutineLightingSnapshot(snap)) return;
  const mode = Number(snap.mode ?? snap.id);
  if (Number.isNaN(mode) || mode === 20) return;
  try {
    window.sessionStorage.setItem(
      PRE_RHYTHM_ROUTINE_SESSION_PREFIX + deviceId,
      JSON.stringify(snap)
    );
    const legacy = rhythmTargetIdLegacyBase(deviceId);
    if (legacy !== deviceId) {
      window.sessionStorage.setItem(
        PRE_RHYTHM_ROUTINE_SESSION_PREFIX + legacy,
        JSON.stringify(snap)
      );
    }
  } catch {
    // ignore
  }
}

export function readPreRhythmRoutineSnapshotForDevice(
  deviceId: string
): Record<string, unknown> | null {
  if (typeof window === "undefined" || !deviceId) return null;
  const ids = new Set<string>([deviceId]);
  const legacy = rhythmTargetIdLegacyBase(deviceId);
  if (legacy !== deviceId) ids.add(legacy);
  for (const id of ids) {
    try {
      const raw = window.sessionStorage.getItem(
        PRE_RHYTHM_ROUTINE_SESSION_PREFIX + id
      );
      if (!raw) continue;
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      if (!parsed || typeof parsed !== "object") continue;
      if (Number(parsed.mode) === 20) continue;
      if (!isValidRoutineLightingSnapshotData(parsed)) continue;
      if (isPlaceholderRoutineLightingSnapshot(parsed)) continue;
      return parsed;
    } catch {
      // ignore
    }
  }
  return null;
}

export function clearPreRhythmRoutineSnapshotForDevice(deviceId: string): void {
  if (typeof window === "undefined" || !deviceId) return;
  const ids = new Set<string>([deviceId]);
  const legacy = rhythmTargetIdLegacyBase(deviceId);
  if (legacy !== deviceId) ids.add(legacy);
  for (const id of ids) {
    try {
      window.sessionStorage.removeItem(PRE_RHYTHM_ROUTINE_SESSION_PREFIX + id);
    } catch {
      // ignore
    }
  }
}

const KEYBOARD_SUB_APP_ROUTINE_SESSION_KEY =
  "keyboard-management:lighting:lastRoutineBase";

/** 子应用灯光页写入的 session 常规灯效（退出桌面端 / 无 IDB 时回退） */
export function readKeyboardSubAppSessionRoutineSnapshot(): Record<
  string,
  unknown
> | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(
      KEYBOARD_SUB_APP_ROUTINE_SESSION_KEY
    );
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    if (!parsed || typeof parsed !== "object") return null;
    if (Number(parsed.mode) === 20) return null;
    if (!isValidRoutineLightingSnapshotData(parsed)) return null;
    if (isPlaceholderRoutineLightingSnapshot(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

const PER_DEVICE_ROUTINE_SESSION_PREFIX =
  "music-management:lighting:deviceRoutine:";

function readPerDeviceRoutineFromSession(
  deviceId: string,
  equipment?: Record<string, unknown> | null
): Record<string, unknown> | null {
  if (typeof window === "undefined" || !deviceId) return null;

  const ids = new Set<string>([deviceId]);
  const legacy = rhythmTargetIdLegacyBase(deviceId);
  if (legacy !== deviceId) ids.add(legacy);

  for (const id of ids) {
    try {
      const raw = window.sessionStorage.getItem(
        PER_DEVICE_ROUTINE_SESSION_PREFIX + id
      );
      if (!raw) continue;
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      const picked = pickRoutineBaseForDeviceRestore(parsed, id, equipment);
      if (picked) return picked;
    } catch {
      // ignore
    }
  }
  return null;
}
